import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  ButtonBase,
  Container,
  Fade,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import RefreshIcon from '@mui/icons-material/Refresh';
import { MaterialReactTable } from 'material-react-table';
import { useApi } from '../../hooks/useApi';
import { cveNumbers } from './cveSummary';
import { actionHint, familyEntries, familyTile, softwareFamilies } from './topSoftware';
import { failedErrorHint, failedErrorText, yesNo } from './updateSummary';
import { CVE, REPLACEMENT, SOFTWARE, UPDATES, cveTiles, deviceUrl, replacementTiles, updateTiles } from './summaryTiles';
import { ageYearsOf, reasonsText, verdictLabel } from './systemInfoSummary';
import { useEndpointSummary } from './useEndpointSummary';

// The Endpoint Devices summary of one account: how its machines stand on Windows updates and on known
// vulnerabilities (CVEs). Click a number to list those machines, click a machine to open its own page.
const DEVICE_TYPES = [
  { value: 'workstation', label: 'Workstations' },
  { value: 'server', label: 'Servers' },
];
const ACCOUNT_KEY = 'endpointResultsAccount';
const TYPE_KEY = 'endpointResultsType';

const readStored = (key) => {
  try {
    return sessionStorage.getItem(key);
  } catch (ex) {
    return null;
  }
};
const store = (key, value) => {
  try {
    sessionStorage.setItem(key, value);
  } catch (ex) {
    // Remembering the choice is a convenience only.
  }
};

// One clickable count: how many machines, and what that means.
function Tile({ tile, selected, onClick }) {
  const theme = useTheme();
  const main = tile.color === 'default' ? theme.palette.text.secondary : theme.palette[tile.color].main;
  return (
    <ButtonBase
      onClick={onClick}
      aria-pressed={selected}
      sx={{
        display: 'block',
        textAlign: 'left',
        minWidth: 150,
        px: 2,
        py: 1.25,
        borderRadius: 2,
        border: `1px solid ${selected ? main : alpha(theme.palette.divider, 0.4)}`,
        borderLeft: `5px solid ${main}`,
        background: selected ? alpha(main, 0.16) : 'transparent',
        '&:hover': { background: alpha(main, 0.1) },
      }}
    >
      <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1.1, color: main }}>{tile.count}</Typography>
      <Typography variant="body2" sx={{ mt: 0.25 }}>{tile.label}</Typography>
    </ButtonBase>
  );
}

function SummaryCard({ title, subtitle, tiles, empty, note, selectedId, onSelect }) {
  return (
    <Paper sx={{ p: 2, borderRadius: 2, height: '100%' }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{title}</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>{subtitle}</Typography>
      {note && <Typography variant="body2" color="text.secondary">{note}</Typography>}
      {!note && empty && (
        <Typography variant="body2" color="text.secondary">No machines have reported yet.</Typography>
      )}
      {!note && !empty && tiles.length > 0 && (
        <Stack direction="row" spacing={1.5} useFlexGap flexWrap="wrap">
          {tiles.map(tile => (
            <Tile key={tile.id} tile={tile} selected={selectedId === tile.id} onClick={() => onSelect(tile.id)} />
          ))}
        </Stack>
      )}
    </Paper>
  );
}

// The programs behind the CVEs of the account's machines, one row per program family, worst first.
function TopSoftwareCard({ families, selectedId, onSelect }) {
  const theme = useTheme();
  const shown = families.slice(0, 15);
  return (
    <Paper sx={{ p: 2, borderRadius: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Top Software Vulnerabilities (CVE)</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        Programs with the worst CVEs, without Windows itself. Critical and high are the most on any one version.
        Click a row to list its machines.
      </Typography>
      <Table size="small" aria-label="Top software vulnerabilities">
        <TableHead>
          <TableRow>
            <TableCell>Software</TableCell>
            <TableCell>Versions</TableCell>
            <TableCell align="right">Machines</TableCell>
            <TableCell align="right">Critical</TableCell>
            <TableCell align="right">High</TableCell>
            <TableCell align="right">Worst score</TableCell>
            <TableCell>Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {shown.map(row => (
            <TableRow
              key={row.key}
              hover
              selected={selectedId === row.key}
              onClick={() => onSelect(row.key)}
              sx={{ cursor: 'pointer' }}
            >
              <TableCell sx={{ fontWeight: 600 }}>{row.family}</TableCell>
              <TableCell sx={{ maxWidth: 260 }}>{row.versions.length > 0 ? `${row.versions.length}: ${row.versions.join(', ')}` : ''}</TableCell>
              <TableCell align="right">{row.machines}</TableCell>
              <TableCell align="right" sx={{ color: row.critical > 0 ? theme.palette.error.main : undefined, fontWeight: row.critical > 0 ? 700 : undefined }}>{row.critical}</TableCell>
              <TableCell align="right" sx={{ color: row.high > 0 ? theme.palette.warning.main : undefined }}>{row.high}</TableCell>
              <TableCell align="right">{row.highestScore || ''}</TableCell>
              <TableCell>{actionHint(row)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {families.length > shown.length && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Showing the worst {shown.length} of {families.length}.
        </Typography>
      )}
    </Paper>
  );
}

function EndpointDevicesPage() {
  const jApi = useApi();
  const theme = useTheme();
  const navigate = useNavigate();

  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [deviceType, setDeviceType] = useState(
    DEVICE_TYPES.some(t => t.value === readStored(TYPE_KEY)) ? readStored(TYPE_KEY) : DEVICE_TYPES[0].value,
  );
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(true);
  const [accountError, setAccountError] = useState(null);
  const [selection, setSelection] = useState(null);   // { kind: 'updates' | 'cve', id } of the clicked tile

  const { loading, error, updates, cve, sysinfo, reload } = useEndpointSummary(selectedAccount?.code, deviceType);

  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        setIsLoadingAccounts(true);
        const response = await jApi.request('GET', 'cma/account');
        const list = (response.data || []).slice().sort((a, b) => a.code.localeCompare(b.code));
        setAccounts(list);
        const savedCode = readStored(ACCOUNT_KEY);
        setSelectedAccount(list.find(a => a.code === savedCode) || list[0] || null);
      } catch (ex) {
        console.error('Failed to load accounts:', ex);
        setAccountError(ex?.toString?.() || 'Failed to load accounts');
      } finally {
        setIsLoadingAccounts(false);
      }
    };
    fetchAccounts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // A different account or device type starts with nothing selected.
  useEffect(() => {
    setSelection(null);
  }, [selectedAccount, deviceType]);

  const updateTileList = useMemo(() => updateTiles(updates.items), [updates.items]);
  const cveTileList = useMemo(() => cveTiles(cve.items), [cve.items]);
  const replacementTileList = useMemo(() => replacementTiles(sysinfo.items), [sysinfo.items]);
  const families = useMemo(() => softwareFamilies(cve.items), [cve.items]);
  const familyTileList = useMemo(() => families.map(familyTile), [families]);

  // The tiles and the machines behind the clicked tile, by the kind of card it is in.
  const tilesOf = (kind) => {
    if (kind === UPDATES) return updateTileList;
    if (kind === CVE) return cveTileList;
    if (kind === SOFTWARE) return familyTileList;
    return replacementTileList;
  };
  const itemsOf = (kind) => {
    if (kind === UPDATES) return updates.items;
    if (kind === CVE || kind === SOFTWARE) return cve.items;
    return sysinfo.items;
  };

  const selectedTile = useMemo(() => {
    if (!selection) return null;
    return tilesOf(selection.kind).find(t => t.id === selection.id) || null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selection, updateTileList, cveTileList, familyTileList, replacementTileList]);

  const listedItems = useMemo(() => {
    if (!selectedTile) return [];
    return itemsOf(selection.kind).filter(selectedTile.matches);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTile, selection, updates.items, cve.items, sysinfo.items]);

  const select = (kind, id) => setSelection(prev => (prev && prev.kind === kind && prev.id === id ? null : { kind, id }));

  const updateColumns = useMemo(() => {
    const field = (name) => (row) => row.results?.[name];
    return [
      { id: 'computer', header: 'Computer', size: 180, accessorFn: row => row.results?.computer || row.deviceId },
      { id: 'statusText', header: 'Summary', size: 300, accessorFn: field('statusText') },
      { id: 'os', header: 'Operating system', size: 200,
        accessorFn: row => [row.results?.['os.name'], row.results?.['os.release']].filter(Boolean).join(' ') },
      { id: 'restart', header: 'Restart required', size: 140, accessorFn: row => yesNo(row.results?.restartRequired) },
      { id: 'daysSinceRestart', header: 'Days since restart', size: 140, accessorFn: field('os.daysSinceRestart') },
      { id: 'daysSinceSecurity', header: 'Days since security update', size: 180, accessorFn: field('daysSinceSecurityUpdate') },
      { id: 'failed', header: 'Failed updates', size: 130, accessorFn: field('summary.failed') },
      { id: 'failedError', header: 'Most common error', size: 170, accessorFn: row => failedErrorText(row) },
      { id: 'failedHint', header: 'What it usually means', size: 380, accessorFn: row => failedErrorHint(row) },
    ];
  }, []);

  const cveColumns = useMemo(() => {
    const n = (key) => (row) => cveNumbers(row)[key];
    return [
      { id: 'computer', header: 'Computer', size: 180, accessorFn: row => row.results?.computerName || row.deviceId },
      { id: 'os', header: 'Operating system', size: 200, accessorFn: row => row.results?.['os.name'] || '' },
      { id: 'critical', header: 'Critical', size: 90, accessorFn: n('critical') },
      { id: 'high', header: 'High', size: 80, accessorFn: n('high') },
      { id: 'medium', header: 'Medium', size: 90, accessorFn: n('medium') },
      { id: 'low', header: 'Low', size: 70, accessorFn: n('low') },
      { id: 'programs', header: 'Programs with CVEs', size: 160, accessorFn: n('programsWithCves') },
      { id: 'unused', header: 'Unused, with CVEs', size: 150, accessorFn: n('unusedWithCves') },
      { id: 'cannotAssess', header: 'Cannot assess', size: 130, accessorFn: n('cannotAssess') },
    ];
  }, []);

  // The machines of one program family: which versions they have and how bad they are.
  const familyId = selection?.kind === SOFTWARE ? selection.id : null;
  const familyRow = useMemo(() => families.find((f) => f.key === familyId), [families, familyId]);
  const softwareColumns = useMemo(() => {
    const entries = (row) => familyEntries(row, familyRow);
    const most = (key) => (row) => Math.max(0, ...entries(row).map(p => Number(p[key]) || 0));
    return [
      { id: 'computer', header: 'Computer', size: 180, accessorFn: row => row.results?.computerName || row.deviceId },
      { id: 'os', header: 'Operating system', size: 200, accessorFn: row => row.results?.['os.name'] || '' },
      { id: 'versions', header: 'Versions', size: 260, accessorFn: row => entries(row).map(p => p.version).filter(Boolean).join(', ') },
      { id: 'critical', header: 'Critical', size: 90, accessorFn: most('critical') },
      { id: 'high', header: 'High', size: 80, accessorFn: most('high') },
      { id: 'score', header: 'Worst score', size: 120, accessorFn: most('highestScore') },
      { id: 'unused', header: 'Unused', size: 100, accessorFn: row => (entries(row).some(p => p.unused) ? 'Yes' : 'No') },
    ];
  }, [familyRow]);

  const replacementColumns = useMemo(() => {
    const field = (name) => (row) => row.results?.[name];
    return [
      { id: 'computer', header: 'Computer', size: 180, accessorFn: row => row.results?.ComputerName || row.deviceId },
      { id: 'verdict', header: 'Verdict', size: 150, accessorFn: row => verdictLabel(row) },
      { id: 'age', header: 'Age (years)', size: 120, accessorFn: row => ageYearsOf(row) ?? '' },
      { id: 'built', header: 'Built', size: 150,
        accessorFn: row => [row.results?.['DeviceAge.estimatedBuildDate'],
          row.results?.['DeviceAge.ageSource'] ? `(${row.results['DeviceAge.ageSource']})` : ''].filter(Boolean).join(' ') },
      { id: 'chassis', header: 'Type', size: 100, accessorFn: field('ReplacementAssessment.chassis') },
      { id: 'model', header: 'Make and model', size: 240,
        accessorFn: row => [row.results?.Manufacturer, row.results?.Model].filter(Boolean).join(' ') },
      { id: 'serial', header: 'Serial', size: 150, accessorFn: field('SerialNumber') },
      { id: 'due', header: 'Replacement due', size: 150, accessorFn: field('ReplacementAssessment.dates.replacementDueDate') },
      { id: 'reasons', header: 'Why', size: 360, accessorFn: row => reasonsText(row) },
    ];
  }, []);

  const handleAccountChange = (e, value) => {
    if (value) store(ACCOUNT_KEY, value.code);
    setSelectedAccount(value);
  };

  const handleTypeChange = (e, value) => {
    if (!value) return;
    store(TYPE_KEY, value);
    setDeviceType(value);
  };

  const machineCount = Math.max(updates.items.length, cve.items.length, sysinfo.items.length);
  const subtitle = (() => {
    if (isLoadingAccounts) return 'Loading...';
    if (!selectedAccount) return 'No accounts available';
    if (loading) return 'Loading...';
    return `${machineCount} machine${machineCount === 1 ? '' : 's'} reporting`;
  })();

  const listKind = selection?.kind;
  const listTitle = selectedTile
    ? `${listedItems.length} machine${listedItems.length === 1 ? '' : 's'}: ${selectedTile.label}`
    : '';

  return (
    <Box sx={{ minHeight: '100vh', pb: 6 }}>
      <Container maxWidth="xl">
        <Fade in timeout={500}>
          <Box sx={{ pt: 3, pb: 2 }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
                px: 2,
                py: 1,
                borderRadius: 2,
                background: alpha(theme.palette.background.paper, 0.6),
                backdropFilter: 'blur(10px)',
                border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              }}
            >
              <Link to="/home" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.5, borderRadius: 1.5,
                  '&:hover': { background: alpha(theme.palette.secondary.main, 0.1) } }}>
                  <HomeRoundedIcon sx={{ fontSize: 20, color: theme.palette.secondary.main }} />
                  <Typography variant="body2" sx={{ color: theme.palette.secondary.main, fontWeight: 600 }}>
                    Home
                  </Typography>
                </Box>
              </Link>
              <Typography variant="body2" color="text.secondary" sx={{ mx: 0.5 }}>/</Typography>
              <Typography variant="body2" sx={{ color: theme.palette.text.primary, fontWeight: 600, px: 1, py: 0.5 }}>
                Endpoint Devices
              </Typography>
            </Box>
          </Box>
        </Fade>

        <Grid container spacing={2}>
          <Grid size={12}>
            <Paper sx={{ p: 2, borderRadius: 2, position: 'relative', overflow: 'hidden' }}>
              <Grid container spacing={2} alignItems="center">
                <Grid size={{ xs: 12, md: 5 }}>
                  <Typography variant="h5" sx={{ fontWeight: 700 }}>Endpoint Devices</Typography>
                  <Typography variant="body2" color="text.secondary">{subtitle}</Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 7 }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" justifyContent="flex-end">
                    <Autocomplete
                      options={accounts}
                      getOptionLabel={(a) => `${a.name} (${a.code})`}
                      value={selectedAccount}
                      onChange={handleAccountChange}
                      isOptionEqualToValue={(a, b) => a.code === b.code}
                      size="small"
                      loading={isLoadingAccounts}
                      sx={{ width: { xs: '100%', sm: 280 } }}
                      renderInput={(params) => <TextField {...params} label="Account" />}
                    />
                    <ToggleButtonGroup exclusive size="small" value={deviceType} onChange={handleTypeChange} aria-label="Device type">
                      {DEVICE_TYPES.map(t => <ToggleButton key={t.value} value={t.value}>{t.label}</ToggleButton>)}
                    </ToggleButtonGroup>
                    <Button component={Link} size="small" to={`/softwarepolicy${selectedAccount ? `?account=${encodeURIComponent(selectedAccount.code)}` : ''}`}>
                      Software policy
                    </Button>
                    <Button component={Link} size="small" to={`/softwaredrift${selectedAccount ? `?account=${encodeURIComponent(selectedAccount.code)}` : ''}`}>
                      Software drift
                    </Button>
                    <Tooltip title="Refresh">
                      <span>
                        <IconButton onClick={reload} disabled={loading || !selectedAccount} size="small" aria-label="Refresh">
                          <RefreshIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                </Grid>
              </Grid>
              {(loading || isLoadingAccounts) && <LinearProgress sx={{ position: 'absolute', bottom: 0, left: 0, right: 0 }} />}
            </Paper>
          </Grid>

          {(accountError || error) && (
            <Grid size={12}>
              <Alert severity="error">{accountError || error}</Alert>
            </Grid>
          )}

          {selectedAccount && !error && (
            <>
              <Grid size={{ xs: 12, lg: 6 }}>
                <SummaryCard
                  title="Windows updates"
                  subtitle="Machines by update status, last 30 days"
                  tiles={updateTileList}
                  empty={updates.items.length === 0}
                  note={updates.note}
                  selectedId={selection?.kind === UPDATES ? selection.id : null}
                  onSelect={(id) => select(UPDATES, id)}
                />
              </Grid>
              <Grid size={{ xs: 12, lg: 6 }}>
                <SummaryCard
                  title="Vulnerabilities (CVE)"
                  subtitle="Machines by their worst known software vulnerability. Windows itself is counted on its own line."
                  tiles={cveTileList}
                  empty={cve.items.length === 0}
                  note={cve.note}
                  selectedId={selection?.kind === CVE ? selection.id : null}
                  onSelect={(id) => select(CVE, id)}
                />
              </Grid>

              <Grid size={12}>
                <SummaryCard
                  title="Replacement and age"
                  subtitle="Machines to replace, or older than 4 years"
                  tiles={replacementTileList}
                  empty={sysinfo.items.length === 0}
                  note={sysinfo.note}
                  selectedId={selection?.kind === REPLACEMENT ? selection.id : null}
                  onSelect={(id) => select(REPLACEMENT, id)}
                />
              </Grid>

              {families.length > 0 && (
                <Grid size={12}>
                  <TopSoftwareCard
                    families={families}
                    selectedId={selection?.kind === SOFTWARE ? selection.id : null}
                    onSelect={(id) => select(SOFTWARE, id)}
                  />
                </Grid>
              )}

              {!selectedTile && (updates.items.length > 0 || cve.items.length > 0 || sysinfo.items.length > 0) && (
                <Grid size={12}>
                  <Typography variant="body2" color="text.secondary">
                    Click a number to list those machines, then click a machine for its updates, software and system info.
                  </Typography>
                </Grid>
              )}

              {selectedTile && (
                <Grid size={12}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                    {listTitle}
                    <Typography component="span" variant="body2" color="text.secondary">
                      {' '}- click a machine to open it
                    </Typography>
                  </Typography>
                  <MaterialReactTable
                    key={`devices-${selectedAccount.code}-${deviceType}-${listKind}-${selectedTile.id}-${listedItems.length}`}
                    columns={listKind === CVE ? cveColumns : listKind === SOFTWARE ? softwareColumns
                      : listKind === REPLACEMENT ? replacementColumns : updateColumns}
                    data={listedItems}
                    enableDensityToggle={false}
                    enablePagination={false}
                    initialState={listKind === CVE || listKind === SOFTWARE
                      ? { sorting: [{ id: 'critical', desc: true }, { id: 'high', desc: true }] }
                      : listKind === REPLACEMENT ? { sorting: [{ id: 'age', desc: true }] } : {}}
                    muiTableBodyRowProps={({ row }) => ({
                      hover: true,
                      onClick: () => navigate(deviceUrl(row.original.deviceId, selectedAccount.code, deviceType, listKind)),
                      sx: { cursor: 'pointer' },
                    })}
                  />
                </Grid>
              )}
            </>
          )}
        </Grid>
      </Container>
    </Box>
  );
}

export default EndpointDevicesPage;
